import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, AlertTriangle, DollarSign, Link2, Activity, Clock } from 'lucide-react';
import { fetchProviderScorecard } from '../api/providers';
import { PageHeader } from '../components/ui/PageHeader';
import { cn } from '../lib/utils';

function formatCompact(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

const statusColors = {
  live: 'bg-success',
  removed: 'bg-destructive',
  '404': 'bg-destructive',
  '5xx': 'bg-destructive',
  redirected: 'bg-warning',
  nofollow_changed: 'bg-warning',
  timeout: 'bg-muted-foreground',
  pending: 'bg-muted-foreground/40',
};

const qualityColors = {
  strong: 'bg-success',
  moderate: 'bg-primary',
  weak: 'bg-warning',
  toxic: 'bg-destructive',
};

export default function ProviderScorecard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['provider-scorecard', id],
    queryFn: () => fetchProviderScorecard(id),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="space-y-6"><div className="h-8 w-48 bg-muted/30 rounded animate-pulse" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted/30 rounded-xl animate-pulse" />)}</div></div>;
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load provider scorecard</p>
        <button onClick={() => navigate('/providers')} className="mt-4 text-sm text-primary hover:underline">Back to Providers</button>
      </div>
    );
  }

  const { provider, scorecard: sc } = data;
  const totalQuality = sc.quality_distribution.strong + sc.quality_distribution.moderate + sc.quality_distribution.weak + sc.quality_distribution.toxic;
  const livePct = sc.total_links > 0 ? Math.round(((sc.status_breakdown.live?.count || 0) / sc.total_links) * 100) : 0;
  const lostCount = (sc.status_breakdown.removed?.count || 0) + (sc.status_breakdown['404']?.count || 0) + (sc.status_breakdown['5xx']?.count || 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={provider.name}
        subtitle="Provider Scorecard"
        actions={
          <button onClick={() => navigate('/providers')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> All Providers
          </button>
        }
      />

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Links', value: sc.total_links, icon: Link2, color: 'text-primary' },
          { label: 'Total Spend', value: sc.total_cost > 0 ? `$${sc.total_cost.toLocaleString()}` : '—', icon: DollarSign, color: 'text-success' },
          { label: '% Live', value: `${livePct}%`, icon: Activity, color: livePct >= 80 ? 'text-success' : livePct >= 50 ? 'text-warning' : 'text-destructive' },
          { label: 'Median Quality', value: sc.median_quality != null ? sc.median_quality.toFixed(0) : '—', icon: Building2, color: sc.median_quality >= 70 ? 'text-success' : sc.median_quality >= 40 ? 'text-warning' : 'text-destructive' },
        ].map((stat, i) => (
          <div key={i} className="glow-card-filled rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <stat.icon className={cn('h-4 w-4', stat.color)} />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <p className={cn('text-2xl font-bold', stat.color)}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="glow-card-filled rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Status Breakdown</h3>
          <div className="flex h-6 rounded-full overflow-hidden bg-muted/30 mb-3">
            {Object.entries(sc.status_breakdown).map(([status, { count }]) => {
              const pct = (count / sc.total_links) * 100;
              if (pct === 0) return null;
              return <div key={status} className={cn('h-full', statusColors[status] || 'bg-muted')} style={{ width: `${pct}%` }} title={`${status}: ${count}`} />;
            })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(sc.status_breakdown).map(([status, { count, pct }]) => (
              <div key={status} className="flex items-center gap-2 text-xs">
                <div className={cn('w-2.5 h-2.5 rounded-full', statusColors[status] || 'bg-muted')} />
                <span className="text-foreground capitalize">{status.replace('_', ' ')}</span>
                <span className="text-muted-foreground ml-auto tabular-nums">{count} ({pct}%)</span>
              </div>
            ))}
          </div>
          {lostCount > 5 && (
            <p className="text-xs text-warning mt-3 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {lostCount} links lost — review provider quality
            </p>
          )}
        </div>

        {/* Quality Distribution */}
        <div className="glow-card-filled rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Quality Distribution</h3>
          <div className="space-y-3">
            {Object.entries(sc.quality_distribution).map(([level, count]) => {
              const pct = totalQuality > 0 ? Math.round((count / totalQuality) * 100) : 0;
              return (
                <div key={level}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground capitalize font-medium">{level}</span>
                    <span className="text-muted-foreground tabular-nums">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2.5 bg-muted/30 rounded-full overflow-hidden">
                    <div className={cn('h-full rounded-full', qualityColors[level])} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time to Loss */}
        <div className="glow-card-filled rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" /> Time to Loss
          </h3>
          {Object.values(sc.time_to_loss).every(v => v === 0) ? (
            <p className="text-sm text-muted-foreground">No lost links yet</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(sc.time_to_loss).map(([bucket, count]) => {
                const totalLost = Object.values(sc.time_to_loss).reduce((s, v) => s + v, 0);
                const pct = totalLost > 0 ? Math.round((count / totalLost) * 100) : 0;
                return (
                  <div key={bucket} className="flex items-center gap-3 text-xs">
                    <span className="w-10 text-muted-foreground">{bucket}</span>
                    <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-destructive/70" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-8 text-right tabular-nums text-foreground">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Concentration Analysis */}
        <div className="glow-card-filled rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Concentration Analysis</h3>
          <div className="space-y-3">
            <div>
              <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5">Top Domains</h4>
              {sc.concentration.top_domains.slice(0, 5).map(d => (
                <div key={d.domain} className="flex items-center gap-2 text-xs py-0.5">
                  <span className="flex-1 truncate text-foreground" title={d.domain}>{d.domain}</span>
                  <span className="text-muted-foreground tabular-nums">{d.count} ({d.pct}%)</span>
                  {d.flagged && <AlertTriangle className="h-3 w-3 text-warning flex-shrink-0" />}
                </div>
              ))}
            </div>
            <div>
              <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5">Top Anchors</h4>
              {sc.concentration.top_anchors.slice(0, 5).map(a => (
                <div key={a.anchor} className="flex items-center gap-2 text-xs py-0.5">
                  <span className="flex-1 truncate text-foreground" title={a.anchor}>{a.anchor}</span>
                  <span className="text-muted-foreground tabular-nums">{a.count} ({a.pct}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Batch History */}
      {sc.batches.length > 0 && (
        <div className="glow-card-filled rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Batch History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-muted-foreground font-medium">Batch</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Links</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Cost</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">% Live</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {sc.batches.map((b, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2 text-foreground font-medium">{b.label}</td>
                    <td className="py-2 text-right tabular-nums text-foreground">{b.count}</td>
                    <td className="py-2 text-right tabular-nums text-foreground">{b.cost > 0 ? `$${b.cost.toLocaleString()}` : '—'}</td>
                    <td className="py-2 text-right">
                      <span className={cn('tabular-nums font-medium', b.live_pct >= 80 ? 'text-success' : b.live_pct >= 50 ? 'text-warning' : 'text-destructive')}>
                        {b.live_pct}%
                      </span>
                    </td>
                    <td className="py-2 text-right text-muted-foreground">{new Date(b.earliest).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
